import {
  createCamelCasedClient,
  createTokenRefreshInterceptor,
  getAffiliateLinkMetrics,
  getAffiliateLinkStats,
  getPaymentSummary,
} from '@metapic-apps/shared'
import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { config } from '@/lib/helpers'
import { notifyUserBanned } from '@/lib/services/banned-user.service'
import { useAuthStore } from '@/stores/auth.stores'

const financeAxios = axios.create({
  baseURL: config.financeApiUrl,
})

financeAxios.interceptors.request.use(
  (request) => {
    const authStore = useAuthStore()

    request.headers.setAuthorization(authStore.authorization || '')
    request.headers.setContentType(request.isFormData ? 'multipart/form-data' : 'application/json')

    return request
  },
  (error: AxiosError) => Promise.reject(error),
)

financeAxios.interceptors.response.use(
  (response) => response,
  (
    error: AxiosError<{
      code?: string
      message?: string
    }>,
  ) => {
    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      notifyUserBanned()
    }

    return Promise.reject(error)
  },
)

createTokenRefreshInterceptor(financeAxios, {
  refreshToken: () => useAuthStore().refreshToken(),
  onRefreshFailure: () => useAuthStore().handleRefreshFailure(),
})

const financeClientFactory = (axiosInstance: AxiosInstance) => ({
  ...getPaymentSummary(axiosInstance),
  ...getAffiliateLinkMetrics(axiosInstance),
  ...getAffiliateLinkStats(axiosInstance),
})

export const financeClient = createCamelCasedClient(financeClientFactory, financeAxios)
